import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import '../styles/StudentProfiles.css';

const API_BASE_URL = process.env.NODE_ENV === 'production'
  ? ''
  : process.env.REACT_APP_API_URL || 'http://localhost:5001';

// Inline SVG icons for a clean, professional look
const Icon = {
  search: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  ),
  plus: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  ),
  user: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" />
    </svg>
  ),
  trash: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    </svg>
  ),
  logout: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><polyline points="16 17 21 12 16 7" /><line x1="21" y1="12" x2="9" y2="12" />
    </svg>
  ),
  book: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" /><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
    </svg>
  ),
  briefcase: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="7" width="20" height="14" rx="2" ry="2" /><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
    </svg>
  ),
  phone: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
    </svg>
  ),
  arrowRight: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" />
    </svg>
  ),
  close: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  )
};

const getInitials = (name = '') => {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

function StudentProfiles({ user, onLogout, onProfileSelect }) {
  const navigate = useNavigate();
  const [profiles, setProfiles] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    studentName: '',
    phoneNumber: '',
    course: '',
    yearsOfExperience: 0,
    currentDesignation: ''
  });

  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchProfiles();
  }, []);

  const fetchProfiles = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(`${API_BASE_URL}/api/profiles`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setProfiles(response.data.data || []);
    } catch (error) {
      console.error('Error fetching profiles:', error);
      alert('Error loading profiles');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddProfile = async (e) => {
    e.preventDefault();
    if (!formData.studentName || !formData.course) {
      alert('Please fill in required fields');
      return;
    }
    try {
      setIsLoading(true);
      await axios.post(`${API_BASE_URL}/api/profiles`, {
        studentName: formData.studentName,
        phoneNumber: formData.phoneNumber,
        course: formData.course,
        yearsOfExperience: parseInt(formData.yearsOfExperience) || 0,
        currentDesignation: formData.currentDesignation
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      setFormData({ studentName: '', phoneNumber: '', course: '', yearsOfExperience: 0, currentDesignation: '' });
      setShowAddModal(false);
      fetchProfiles();
    } catch (error) {
      console.error('Error creating profile:', error);
      alert('Error creating profile');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteProfile = async (id) => {
    if (!window.confirm('Are you sure you want to delete this profile?')) return;
    try {
      setIsLoading(true);
      await axios.delete(`${API_BASE_URL}/api/profiles/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchProfiles();
    } catch (error) {
      console.error('Error deleting profile:', error);
      alert('Error deleting profile');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectProfile = (profile) => {
    onProfileSelect(profile.id, profile);
    navigate(`/profile/${profile.id}`);
  };

  const handleGuestContinue = () => {
    onProfileSelect(null, null);
    navigate('/search');
  };

  const filteredProfiles = profiles.filter(profile =>
    profile.studentName.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="sp-page">
      {/* Top navigation bar */}
      <header className="sp-navbar">
        <div className="sp-brand">
          <div className="sp-brand-mark">FQA</div>
          <div className="sp-brand-text">
            <h1>Student Profiles</h1>
            <p>Manage your interview preparation profiles</p>
          </div>
        </div>
        <div className="sp-nav-actions">
          <span className="sp-user-chip">{Icon.user}<span>{user}</span></span>
          <button className="sp-btn sp-btn-ghost" onClick={onLogout}>
            {Icon.logout}<span>Logout</span>
          </button>
        </div>
      </header>

      <main className="sp-container">
        {/* Toolbar */}
        <div className="sp-toolbar">
          <div className="sp-search">
            <span className="sp-search-icon">{Icon.search}</span>
            <input
              type="text"
              placeholder="Search profiles by name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <div className="sp-toolbar-actions">
            <button className="sp-btn sp-btn-outline" onClick={handleGuestContinue}>
              {Icon.user}<span>Continue as Guest</span>
            </button>
            <button className="sp-btn sp-btn-primary" onClick={() => setShowAddModal(true)}>
              {Icon.plus}<span>Add Profile</span>
            </button>
          </div>
        </div>

        {/* Count */}
        {!isLoading && filteredProfiles.length > 0 && (
          <p className="sp-count">{filteredProfiles.length} profile{filteredProfiles.length !== 1 ? 's' : ''}</p>
        )}

        {/* Grid */}
        {isLoading ? (
          <div className="sp-loading"><span className="sp-spinner" />Loading profiles...</div>
        ) : filteredProfiles.length === 0 ? (
          <div className="sp-empty">
            <div className="sp-empty-icon">{Icon.user}</div>
            <h3>{searchQuery ? 'No matching profiles' : 'No profiles yet'}</h3>
            <p>{searchQuery ? 'Try a different search term' : 'Create your first profile to get started'}</p>
            {!searchQuery && (
              <button className="sp-btn sp-btn-primary" onClick={() => setShowAddModal(true)}>
                {Icon.plus}<span>Add Profile</span>
              </button>
            )}
          </div>
        ) : (
          <div className="sp-grid">
            {filteredProfiles.map(profile => (
              <div key={profile.id} className="sp-card">
                <div className="sp-card-top">
                  <div className="sp-avatar">{getInitials(profile.studentName)}</div>
                  <div className="sp-card-head">
                    <h3>{profile.studentName}</h3>
                    <span className="sp-card-role">{profile.currentDesignation || 'No designation'}</span>
                  </div>
                  <button className="sp-icon-btn sp-danger" title="Delete profile" onClick={() => handleDeleteProfile(profile.id)}>
                    {Icon.trash}
                  </button>
                </div>

                <div className="sp-card-meta">
                  <span className="sp-chip">{Icon.book}{profile.course}</span>
                  <span className="sp-chip">{Icon.briefcase}{profile.yearsOfExperience} yr{profile.yearsOfExperience !== 1 ? 's' : ''}</span>
                  {profile.phoneNumber && <span className="sp-chip">{Icon.phone}{profile.phoneNumber}</span>}
                </div>

                <button className="sp-btn sp-btn-primary sp-card-cta" onClick={() => handleSelectProfile(profile)}>
                  <span>Open Profile</span>{Icon.arrowRight}
                </button>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Add Profile Modal */}
      {showAddModal && (
        <div className="sp-modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="sp-modal" onClick={(e) => e.stopPropagation()}>
            <div className="sp-modal-header">
              <div>
                <h2>Add New Profile</h2>
                <p>Create a student profile for interview prep</p>
              </div>
              <button className="sp-icon-btn" onClick={() => setShowAddModal(false)}>{Icon.close}</button>
            </div>

            <form onSubmit={handleAddProfile} className="sp-modal-body">
              <div className="sp-field">
                <label>Student Name <span className="sp-req">*</span></label>
                <input type="text" required value={formData.studentName}
                  onChange={(e) => setFormData({ ...formData, studentName: e.target.value })}
                  placeholder="Enter full name" />
              </div>

              <div className="sp-field-row">
                <div className="sp-field">
                  <label>Course / Technology <span className="sp-req">*</span></label>
                  <input type="text" required value={formData.course}
                    onChange={(e) => setFormData({ ...formData, course: e.target.value })}
                    placeholder="e.g., Java, Python, QA" />
                </div>
                <div className="sp-field">
                  <label>Years of Experience</label>
                  <input type="number" min="0" max="50" value={formData.yearsOfExperience}
                    onChange={(e) => setFormData({ ...formData, yearsOfExperience: e.target.value })}
                    placeholder="0" />
                </div>
              </div>

              <div className="sp-field-row">
                <div className="sp-field">
                  <label>Current Designation</label>
                  <input type="text" value={formData.currentDesignation}
                    onChange={(e) => setFormData({ ...formData, currentDesignation: e.target.value })}
                    placeholder="e.g., QA Engineer, SDET" />
                </div>
                <div className="sp-field">
                  <label>Phone Number</label>
                  <input type="tel" value={formData.phoneNumber}
                    onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })}
                    placeholder="Optional" />
                </div>
              </div>

              <div className="sp-modal-footer">
                <button type="button" className="sp-btn sp-btn-ghost" onClick={() => setShowAddModal(false)}>Cancel</button>
                <button type="submit" className="sp-btn sp-btn-primary" disabled={isLoading}>
                  {isLoading ? 'Creating...' : 'Create Profile'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default StudentProfiles;
